import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";

import { Card } from "@/components/ui/card";

// FIN-4 — Donut de distribuição da carteira por asset_class.
// Consome props.byClass (data direto do hook /by-class).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface-overlay border border-subtle rounded-lg px-3 py-2 shadow-2xl">
      <p className="text-caption font-semibold text-foreground mb-0.5">{d.class}</p>
      <p className="text-micro text-foreground-muted tabular-nums">
        {fmtBRL(d.value)} ({d.pct}%)
      </p>
    </div>
  );
}

export default function DistribuicaoCarteiraDonut({ byClass }) {
  const total = Number(byClass?.total ?? 0);
  const classes = (byClass?.classes || []).filter(c => c.value > 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 min-h-[280px] flex flex-col" data-testid="invest-donut">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-md bg-brand-cyan/15 flex items-center justify-center shrink-0">
          <PieIcon className="w-4 h-4 text-brand-cyan" aria-hidden="true" />
        </div>
        <h3 className="font-display text-base md:text-lg font-bold text-foreground">
          Distribuição da carteira
        </h3>
      </div>

      {classes.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-foreground-muted">
          <PieIcon className="w-8 h-8 opacity-40" aria-hidden="true" />
          <p className="text-caption">Adicione investimentos pra visualizar a distribuição</p>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-4 flex-1 min-h-[220px]">
          <div className="w-full md:w-[55%] h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={classes}
                  dataKey="value"
                  nameKey="class"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  stroke="rgba(0,0,0,0.4)"
                  strokeWidth={2}
                >
                  {classes.map((c) => (
                    <Cell key={c.class} fill={c.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1 w-full md:w-auto space-y-1.5 max-h-[200px] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-subtle/40 pb-1.5 mb-1.5">
              <span className="text-micro text-foreground-muted uppercase font-semibold" style={{ letterSpacing: "0.08em" }}>
                Total
              </span>
              <span className="font-mono font-semibold text-foreground tabular-nums">
                {fmtBRL(total)}
              </span>
            </div>
            {classes.map(c => (
              <div key={c.class} className="flex items-center gap-2 text-caption">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: c.color }}
                  aria-hidden="true"
                />
                <span className="flex-1 truncate text-foreground">{c.class}</span>
                <span className="text-foreground-muted tabular-nums">{c.pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</span>
                <span className="font-mono font-medium text-foreground tabular-nums w-24 text-right">
                  {fmtBRL(c.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
